import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  type LoginCredentials,
  type AuthState,
  type AuthContextValue,
  AuthError,
} from '../../types/auth';
import { AuthService, AuthValidationService } from '../../components/auth/services';

/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Custom hook to access authentication context
 * Throws error if used outside AuthProvider
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Custom hook that provides authentication state and methods
 * This is the main hook that manages all authentication logic
 */
export const useAuthState = (): AuthContextValue => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  /**
   * Initialize authentication state from stored data
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = AuthService.getStoredUser();
        const isAuthenticated = AuthService.isAuthenticated();

        console.log('Auth initialization - User:', storedUser, 'Authenticated:', isAuthenticated);

        setAuthState({
          user: storedUser,
          isAuthenticated,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error initializing auth:', error);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Failed to initialize authentication',
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await AuthService.login(credentials);
      
      // Get user from storage since it's stored during login
      const user = AuthService.getStoredUser();
      const isAuthenticated = AuthService.isAuthenticated();
      
      console.log('After login - User:', user, 'Authenticated:', isAuthenticated);
      
      setAuthState({
        user,
        isAuthenticated,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      
      if (error instanceof AuthError) {
        errorMessage = AuthValidationService.getAuthErrorMessage(error.code);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      // Re-throw error so components can handle it if needed
      throw error;
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      await AuthService.logout();
    } catch (error) {
      console.warn('Logout warning:', error);
      // Continue with logout even if server call fails
    } finally {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  /**
   * Clear error function
   */
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    logout,
    clearError,
  };
};